import { useCallback, useMemo, useRef, useState } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { cloneDeep } from "lodash";

import IgnoreTableIcon from "@assets/ignore-table.svg";
import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { GenericTooltip } from "@components/tooltip-container";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { type Compare } from "@features/sales-v3/hooks/use-chart-state";
import { OptionWithSubmenu } from "@features/sales-v3/shared/breakdown-selector/option-with-submenu";
import { breakdownComparator } from "@features/sales-v3/utils/breakdown-comparator";
import {
  FloatingDelayGroup,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { useFuzzySearch } from "@hooks/use-fuzzy-search";
import { type CategoricalBreakdownConfig } from "@north-beam/nb-common";
import { PrimaryButton } from "@shared/buttons";
import { Checkbox } from "@shared/checkbox";
import { useVirtualizer } from "@tanstack/react-virtual";

import { ManageFiltersButton } from "./manage-filters-button";
import { MetricSearch } from "./metric-search";

export const MetricFilters = ({
  metricFilters,
  ignoreTableFilters,
  updateMetricFilters,
  isAddFilterDisabled = false,
}: {
  metricFilters: Compare["filters"];
  ignoreTableFilters: Compare["ignoreTableFilters"];
  updateMetricFilters: (
    metricFilters: Compare["filters"],
    ignoreTableFilters: Compare["ignoreTableFilters"],
  ) => void;
  isAddFilterDisabled?: boolean;
}) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const scrollElRef = useRef<HTMLDivElement>(null);

  const changeMenuOpen = (value: boolean) => {
    if (value) {
      setSelectedMetricFilters((prevSelectedMetricFilters) => {
        if (prevSelectedMetricFilters.length) {
          return prevSelectedMetricFilters;
        }
        return metricFilters;
      });
      setSelectedIgnoreTableFilters((prev) => {
        if (prev) {
          return prev;
        }
        return ignoreTableFilters;
      });
    } else {
      updateMetricFilters(selectedMetricFilters, selectedIgnoreTableFilters);
      setSelectedMetricFilters([]);
      setSelectedIgnoreTableFilters(false);
      clearSearch();
    }

    setIsMenuOpen(value);
  };

  const [selectedMetricFilters, setSelectedMetricFilters] = useState<
    Compare["filters"]
  >([]);
  const [selectedIgnoreTableFilters, setSelectedIgnoreTableFilters] =
    useState<Compare["ignoreTableFilters"]>(false);

  const [filteredOptions, { search, setSearch, clearSearch }] = useFuzzySearch(
    breakdownConfigs,
    {
      searchKeys: ["name"],
    },
  );

  const sortedOptions = useMemo(
    () =>
      [...filteredOptions].sort((a, b) => breakdownComparator(a.name, b.name)),
    [filteredOptions],
  );

  const selectMetricFilter = useCallback(
    (
      breakdown: CategoricalBreakdownConfig,
      choices: CategoricalBreakdownConfig["choices"][number][],
    ) => {
      let metricFiltersCopy = cloneDeep(selectedMetricFilters);
      const filterIndex = metricFiltersCopy.findIndex(
        ({ breakdown: { key } }) => key === breakdown.key,
      );

      const metricFilter = {
        breakdown,
        values: choices,
      };

      if (filterIndex === -1) {
        metricFiltersCopy.push(metricFilter);
      } else {
        metricFiltersCopy[filterIndex] = metricFilter;
      }

      // Removes the metric filter if there are no choices
      metricFiltersCopy = metricFiltersCopy.filter(
        ({ values }) => values.length,
      );
      setSelectedMetricFilters(metricFiltersCopy);
    },
    [selectedMetricFilters, setSelectedMetricFilters],
  );

  const removeAllMetricFilters = () =>
    updateMetricFilters([], selectedIgnoreTableFilters);

  const rowVirtualizer = useVirtualizer({
    count: sortedOptions.length,
    getScrollElement: () => scrollElRef.current,
    estimateSize: () => 40,
    overscan: 5,
  });

  const { refs, floatingStyles, context } = useFloating({
    open: isMenuOpen,
    onOpenChange: changeMenuOpen,
    transform: false,
    placement: "bottom-start",
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  return (
    <div className="mt-2 flex items-center gap-2">
      <div className="flex items-center">
        <i className="fa-regular fa-sharp fa-arrow-turn-down-right text-gray text-sm mr-1.5" />
        <div>
          <div ref={refs.setReference} {...getReferenceProps()}>
            <ManageFiltersButton
              isMenuOpen={isMenuOpen}
              isDisabled={isAddFilterDisabled}
              onClick={() => setIsMenuOpen(true)}
              removeAllMetricFilters={() => {
                prettyLogEventPage({
                  domain: "Charts",
                  action: "onClick",
                  additionalActionInfo: "Remove Metric Filter",
                });

                removeAllMetricFilters();
              }}
              metricFilters={metricFilters}
              ignoreTableFilters={ignoreTableFilters}
            />
          </div>
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                transition={{ ease: "easeOut", duration: 0.2 }}
                initial={{ opacity: 0, translateY: "0.25rem" }}
                animate={{ opacity: 1, translateY: 0 }}
                exit={{ opacity: 0, translateY: "0.25rem" }}
                ref={refs.setFloating}
                style={floatingStyles}
                className={clsx(
                  "absolute z-10 bg-white rounded-lg elevation-1 text-sm pt-3 pb-2",
                )}
                {...getFloatingProps()}
              >
                <div className="mb-1 border-b border-gray-2">
                  <MetricSearch value={search} onChange={setSearch} />
                  <div className="px-4 py-2">
                    <GenericTooltip content="Metric ignores table filters">
                      <Checkbox
                        className="mr-0.5"
                        label="Ignore table filters"
                        value={selectedIgnoreTableFilters}
                        onChange={(val) => setSelectedIgnoreTableFilters(val)}
                      />
                    </GenericTooltip>
                  </div>
                </div>
                {sortedOptions.length === 0 && (
                  <div className="px-4 py-2 text-disabled">
                    NO RESULTS FOUND
                  </div>
                )}
                <div
                  ref={scrollElRef}
                  className="max-h-[300px] overflow-y-auto floatingui-scroll"
                >
                  <div
                    style={{
                      position: "relative",
                      height: `${rowVirtualizer.getTotalSize()}px`,
                    }}
                  >
                    <FloatingDelayGroup delay={{ open: 0, close: 2000 }}>
                      {rowVirtualizer.getVirtualItems().map((e) => {
                        const row = sortedOptions[e.index];

                        return (
                          <OptionWithSubmenu
                            key={e.key}
                            style={{ transform: `translateY(${e.start}px)` }}
                            row={row}
                            excludeNotSet
                            selectedValues={(
                              selectedMetricFilters.find(
                                (v) => v.breakdown.key === row.key,
                              )?.values || []
                            ).map((v) => v.value)}
                            onChange={(values) => {
                              const choices = row.choices.filter((v) =>
                                values.includes(v.value),
                              );

                              prettyLogEventPage({
                                domain: "Charts",
                                action: "onSelect",
                                additionalActionInfo: "Select Metric Filter",
                                data: {
                                  pageContext: { metricFilters: choices },
                                },
                              });

                              selectMetricFilter(row, choices);
                            }}
                            onReset={() => {
                              prettyLogEventPage({
                                domain: "Charts",
                                action: "onSelect",
                                additionalActionInfo: "Reset Metric Filter",
                              });

                              selectMetricFilter(row, []);
                            }}
                          />
                        );
                      })}
                    </FloatingDelayGroup>
                  </div>
                </div>
                <div className="border-t order-gray-1 px-4 pt-2 pb-0.5 flex justify-center space-x-1">
                  <PrimaryButton
                    className={clsx(
                      "hover:bg-primary hover:text-white font-bold",
                      "bg-cosmic-3 text-primary flex-grow",
                    )}
                    onClick={() => changeMenuOpen(false)}
                  >
                    APPLY
                  </PrimaryButton>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      {ignoreTableFilters && (
        <GenericTooltip noInfoCircle content="Metric ignores table filters">
          <div className="bg-interstellar-3 rounded-lg w-7 h-7 flex items-center justify-center">
            <img
              className="w-4 ml-1 mb-1"
              alt="data-export-logo"
              src={IgnoreTableIcon}
            />
          </div>
        </GenericTooltip>
      )}
    </div>
  );
};
